import { Col, Row } from '@opendoor/atoms/box';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconStar, IconStarFilled, IconStarHalfFilled } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';
import type { IFcTrustBanner } from 'declarations/contentful';

const ReviewStars = ({ rating }: { rating: number }) => {
  const rounded = Math.round(rating * 2) / 2;
  const fullStars = Math.floor(rounded);
  const hasHalf = rounded % 1 !== 0;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return (
    <Row className="items-center gap-1">
      {Array.from({ length: fullStars }, (_, i) => (
        <IconStarFilled
          key={`full-${i}`}
          size={24}
          className="text-od-content-primary"
          aria-hidden="true"
        />
      ))}
      {hasHalf && (
        <IconStarHalfFilled
          key="half"
          size={24}
          className="text-od-content-primary"
          aria-hidden="true"
        />
      )}
      {Array.from({ length: emptyStars }, (_, i) => (
        <IconStar
          key={`empty-${i}`}
          size={24}
          className="text-od-content-primary"
          aria-hidden="true"
        />
      ))}
    </Row>
  );
};

const RenderFCTrustBanner = (entry: IFcTrustBanner) => {
  const { fields } = entry;

  const target = fields.linkUrl?.startsWith('#') ? undefined : '_blank';
  const rel = fields.linkUrl?.startsWith('#') ? undefined : 'noopener noreferrer';

  return (
    <Col className="w-full items-center bg-od-bg-tertiary">
      <Col className="w-full flex-1 items-center px-6 lg:max-w-350 lg:px-20">
        <Col
          className={cn(
            'w-full items-center py-6 md:hidden',
            fields.reviewStars > 0 ? 'gap-6' : 'gap-3',
          )}
        >
          <Col className="w-full items-center gap-3">
            <Col className="items-center">
              <Text as="p" className="text-center text-label-sm text-od-content-primary">
                {fields.textSection1Headline}
              </Text>
              {fields.textSection1Description && (
                <Text as="p" className="text-center text-label-sm text-od-content-primary">
                  {fields.textSection1Description}
                </Text>
              )}
            </Col>
            <Col className="items-center">
              <Text as="p" className="text-center text-label-sm text-od-content-primary">
                {fields.textSection2Headline}
              </Text>
              {fields.textSection2Description && (
                <Text as="p" className="text-center text-label-sm text-od-content-primary">
                  {fields.textSection2Description}
                </Text>
              )}
            </Col>
          </Col>
          <Col className="items-center gap-1">
            {fields.reviewStars > 0 && <ReviewStars rating={fields.reviewStars} />}
            <Link
              href={fields.linkUrl}
              target={target}
              rel={rel}
              analyticsName="opendoor-reviews"
              className="text-label-sm text-od-content-primary hover:text-od-content-primary"
            >
              {fields.linkText}
            </Link>
          </Col>
        </Col>
        <Row className="hidden items-center gap-4 py-7 md:flex">
          {fields.reviewStars > 0 && <ReviewStars rating={fields.reviewStars} />}

          <Text as="p" className="text-label-xl text-od-content-primary">
            {fields.textSection1Headline}
            {fields.textSection1Description ? ` ${fields.textSection1Description}` : ''} |{' '}
            {fields.textSection2Headline}
            {fields.textSection2Description ? ` ${fields.textSection2Description}` : ''} |{' '}
            <Link
              href={fields.linkUrl}
              target={target}
              rel={rel}
              analyticsName="opendoor-reviews"
              className="text-label-xl text-od-content-primary hover:text-od-content-primary"
            >
              {fields.linkText}
            </Link>
          </Text>
        </Row>
      </Col>
    </Col>
  );
};

const FCTrustBanner: EntryComponent<IFcTrustBanner> = {
  render: RenderFCTrustBanner,
};

export default FCTrustBanner;
